import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { ColDef } from "ag-grid-community";
import { Copy, Download, Eye, MoreHorizontal, Search } from "lucide-react";
import { toast } from "react-toastify";

import PaginationLayout from "../components/PaginationLayout";
import henceforthApi from "../utils/henceforthApi";
import { GlobalContext } from "../context/Provider";
import DownloadfileModal from "../components/common/downloadfileModal";
import henceofrthEnums from "../utils/henceofrthEnums";
import { downloadExcelFile, ExportColumn } from "../components/common/ExportButton";

import AgGridTable from "../components/ui/AgGridTable";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const rideExportColumns: ExportColumn[] = [
  { header: "Sr No.", key: "sr_no", type: "number", width: 8 },
  { header: "Driver Name", key: "driver_name", type: "text", width: 18 },
  { header: "Driver Email", key: "driver_email", type: "text", width: 22 },
  { header: "Ride Date", key: "date", type: "date", width: 18 },
  { header: "Price", key: "price", type: "currency", isAmount: true, width: 14 },
  { header: "Status", key: "status", type: "text", width: 14 },
  { header: "Pick up Location", key: "pickup", type: "text", width: 28 },
  { header: "Drop off Location", key: "drop", type: "text", width: 28 },
];

interface driver {
  name: string;
  email: string;
}

interface list {
  driver_detail: driver;
  date: string;
  status: string;
  address_from: string;
  address_to: string;
  price: number;
  currency: string;
  _id: string;
}

function RideListing() {
  const navigate = useNavigate();
  const location = useLocation();
  const limit = 10;
  const { authState, handlePagination, handleFilter, handleError } = useContext(GlobalContext);
  const params: any = useParams();
  const newParam = new URLSearchParams(location.search);
  const query = new URLSearchParams(location.search);

  const [loading, setLoading] = useState(false);
  const [pageData, setPageData] = useState<Array<list>>([]);
  const [totalCount, setTotalCount] = useState(0);

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  const downloadCsv = async (start_date: string, end_date: string) => {
    try {
      let apiRes = await henceforthApi.Rides.export(
        moment(start_date).format("DD/MM/YYYY"),
        moment(end_date).format("DD/MM/YYYY")
      );
      let data = apiRes?.data?.publish_rides || [];

      const rows = data.map((res: any, index: number) => ({
        sr_no: index + 1,
        driver_name: res.driver_detail?.name || "N/A",
        driver_email: res.driver_detail?.email || "N/A",
        date: res.date ? Number(res.date) : "",
        price: Number(res.price || 0),
        status: res.status || "N/A",
        pickup: (res.address_from || "").split(",").join(" "),
        drop: (res.address_to || "").split(",").join(" "),
      }));

      await downloadExcelFile({
        fileName: `created_rides_${moment().format("YYYY-MM-DD")}`,
        sheetName: "Created Rides",
        columns: rideExportColumns,
        data: rows,
      });

      let closeModal = document.getElementById("closeModal");
      if (closeModal) {
        closeModal.click();
      }
    } catch (error: any) {
      console.error("Created rides export error:", error);
    }
  };

  useEffect(() => {
    let q = query.toString();
    const init = async () => {
      setLoading(true);
      try {
        let apiRes = await henceforthApi.Rides.getListing(
          newParam.toString(),
          q,
          Number(params.page) - 1,
          limit
        );
        setTotalCount(apiRes.data.count || 0);
        setPageData(apiRes.data.publish_rides || []);
      } catch (error: any) {
        setPageData([]);
        setTotalCount(0);
        handleError(error);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [params.page, newParam.get("search"), query.get("status")]);

  const formatPrice = (price: number | undefined, currency: string) => {
    if (price === undefined || price === null) return "N/A";
    const symbol =
      currency === henceofrthEnums.CurrencyType.NGN
        ? "₦ "
        : currency === henceofrthEnums.CurrencyType.INR
          ? "₹ "
          : "$ ";
    return `${symbol}${price.toLocaleString()}`;
  };

  const getStatusBadge = (status: string) => {
    const s = (status || "").toUpperCase();
    if (s === "ACTIVE" || s === "COMPLETED" || s === "CONFIRMED") {
      return <Badge variant="success">{s}</Badge>;
    }
    if (s === "PENDING" || s === "ONGOING") {
      return <Badge variant="warning">{s}</Badge>;
    }
    if (s === "CANCELLED" || s === "DECLINED") {
      return <Badge variant="destructive">{s}</Badge>;
    }
    return <Badge variant="secondary">{s || "UNKNOWN"}</Badge>;
  };

  const columnDefs = useMemo<ColDef<list>[]>(() => {
    const pageNum = Number(params.page) || 1;
    return [
      {
        headerName: "Sr No.",
        field: "_id",
        width: 75,
        minWidth: 70,
        maxWidth: 85,
        suppressSizeToFit: true,
        valueGetter: (p) => {
          if (!p.node || p.node.rowIndex === null || p.node.rowIndex === undefined) return "";
          return (pageNum - 1) * limit + (p.node.rowIndex + 1);
        },
      },
      {
        headerName: "Date",
        field: "date",
        minWidth: 130,
        flex: 1,
        cellRenderer: (p: any) => {
          if (!p.value) return <span className="text-slate-400">N/A</span>;
          return (
            <span className="text-slate-600 text-sm">
              {moment(Number(p.value)).utc(true).format("DD MMM YYYY")}
            </span>
          );
        },
      },
      {
        headerName: "Driver",
        field: "driver_detail.name" as any,
        minWidth: 220,
        flex: 1.6,
        cellRenderer: (p: any) => {
          const name = p.data?.driver_detail?.name;
          const email = p.data?.driver_detail?.email;
          return (
            <div className="flex flex-col justify-center h-full leading-tight">
              <span className="font-semibold text-slate-800 text-sm">
                {name || "Not Available"}
              </span>
              <span className="truncate max-w-[200px] text-xs text-slate-500">
                {email || "Not Available"}
              </span>
            </div>
          );
        },
      },

      {
        headerName: "Pick up Location",
        field: "address_from",
        minWidth: 180,
        flex: 1.5,
        cellRenderer: (p: any) => {
          return (
            <span className="text-slate-600 text-sm truncate block" title={p.value}>
              {p.value || "Not available"}
            </span>
          );
        },
      },
      {
        headerName: "Drop off Location",
        field: "address_to",
        minWidth: 180,
        flex: 1.5,
        cellRenderer: (p: any) => {
          return (
            <span className="text-slate-600 text-sm truncate block" title={p.value}>
              {p.value || "Not available"}
            </span>
          );
        },
      },
      {
        headerName: "Price",
        field: "price",
        minWidth: 110,
        flex: 0.9,
        cellRenderer: (p: any) => {
          return (
            <span className="font-semibold text-slate-900 text-sm">
              {formatPrice(p.value, p.data?.currency)}
            </span>
          );
        },
      },
      {
        headerName: "Status",
        field: "status",
        minWidth: 130,
        cellRenderer: (p: any) => {
          return (
            <div className="flex items-center h-full">
              {getStatusBadge(p.value)}
            </div>
          );
        },
      },
      {
        headerName: "Actions",
        width: 90,
        minWidth: 80,
        maxWidth: 100,
        pinned: "right",
        cellRenderer: (p: any) => {
          const row = p.data;
          const rideId = row?._id;

          return (
            <div className="flex items-center justify-center h-full">
              <Link
                to={`/ride-details/${rideId}`}
                title="View Details"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-[#006747] hover:bg-[#006747]/10 transition-colors"
              >
                <Eye className="w-4 h-4" />
              </Link>
            </div>
          );
        },
      },
    ];
  }, [params.page]);

  return (
    <>
      {/* Compact Single Toolbar Row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        {/* Left: Search Input */}
        <div className="relative w-64">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search driver name..."
            value={newParam.get("search") || ""}
            onChange={(e) => handleFilter("search", e.target.value)}
            className="search-icon-input h-9 text-sm bg-white border-slate-200 focus:bg-white shadow-2xs"
          />
        </div>

        {/* Right: Status Filter + Export Button */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Status Filter */}
          <div className="w-40">
            <Select
              value={query.get("status") || "ALL"}
              onValueChange={(val) => handleFilter("status", val === "ALL" ? "" : val)}
            >
              <SelectTrigger className="h-9 text-sm bg-white shadow-2xs">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Status</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="ONGOING">Ongoing</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
                <SelectItem value="DECLINED">Declined</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Export Button */}
          <Button
            variant="default"
            size="sm"
            data-bs-toggle="modal"
            data-bs-target="#exampleModal"
            className="h-9 px-4 text-sm gap-1.5 shadow-xs"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </Button>
        </div>
      </div>

      {/* AG Grid Table */}
      <AgGridTable
        rowData={pageData}
        columnDefs={columnDefs}
        loading={loading}
        domLayout="autoHeight"
        emptyMessage="No created rides found"
      />

      <div className="mt-3">
        <PaginationLayout
          count={totalCount}
          data={pageData}
          page={Number(params.page)}
          limit={limit}
          loading={loading}
          onPageChange={(val) => handlePagination(val)}
        />
      </div>

      <DownloadfileModal data={downloadCsv} />
    </>
  );
}

export default RideListing;
